import { FormCentralizer, Loading } from '@/components';
import {
  useOrganizationRecursive,
  useUpdateOrganization,
} from '@/features/tenant-manager/hooks';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { OrganizationForm } from './OrganizationForm';

export const UpdateOrganizationPage: FC = () => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { id } = useId();
  const { mutateAsync: updateOrganization } = useUpdateOrganization();
  const navigate = useNavigate();

  const organizations = useOrganizationRecursive({ id });
  const currentOrganization = organizations.data?.at(-1);
  const upperOrganizationIds = organizations.data
    ?.slice(0, organizations.data.length - 1)
    .map((d) => d.id);

  if (organizations.isLoading || !currentOrganization) {
    return <Loading />;
  }

  return (
    <FormCentralizer>
      <OrganizationForm
        id={currentOrganization.id}
        isIntegrated={currentOrganization.isIntegrated}
        initialValues={{
          name: currentOrganization.name,
          code: currentOrganization.code,
          upperOrganizationIds,
          roleId: currentOrganization.roleId,
          period: [
            currentOrganization.startYear
              ? dayjs().year(currentOrganization.startYear)
              : undefined,
            currentOrganization.endYear
              ? dayjs().year(currentOrganization.endYear)
              : undefined,
          ],
          externalSystemCode: currentOrganization.externalSystemCode,
        }}
        onSubmit={async (values) => {
          try {
            await updateOrganization({ id, ...values });

            await Promise.all([
              openMessage('success', t('message-save-completed')),
              (async () => {
                navigate(`/tenant-manager/organizations/${id}`, {
                  replace: true,
                });
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-save-failed'));
          }
        }}
      />
    </FormCentralizer>
  );
};
